import React from "react";
import {
  AbsoluteFill,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";

export const FreshNeoBrutalist: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 1. Snappy Brutalist Springs & Cuts
  const introSpring = spring({
    frame,
    fps,
    config: { damping: 12, stiffness: 180 },
  });

  const tapeSlide = interpolate(frame, [0, 200], [0, -400]);

  // Headline mask entrance (Frame 5 to 25)
  const headTranslate = interpolate(frame, [5, 25], [120, 0], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Price badge pop with heavy overshoot (Frame 20)
  const badgeScale = spring({
    frame: frame - 20,
    fps,
    config: { damping: 10, stiffness: 160 },
  });

  // Micro glitch tick on frame intervals
  const isGlitch = frame % 30 >= 0 && frame % 30 <= 2;
  const glitchX = isGlitch ? (frame % 2 === 0 ? 6 : -6) : 0;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#070709",
        color: "#FFFFFF",
        fontFamily: "'Anton', 'Bebas Neue', 'Impact', sans-serif",
        overflow: "hidden",
        transform: `translateX(${glitchX}px)`,
      }}
    >
      {/* 1. Background Grid & Brutalist Accents */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(204,255,0,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(204,255,0,0.06) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          opacity: 0.8,
        }}
      />

      {/* Crosshair Markers */}
      <div style={{ position: "absolute", top: 40, left: 40, color: "#CCFF00", fontSize: 24 }}>+</div>
      <div style={{ position: "absolute", top: 40, right: 40, color: "#CCFF00", fontSize: 24 }}>+</div>
      <div style={{ position: "absolute", bottom: 40, left: 40, color: "#CCFF00", fontSize: 24 }}>+</div>
      <div style={{ position: "absolute", bottom: 40, right: 40, color: "#CCFF00", fontSize: 24 }}>+</div>

      {/* 2. Top Hazard Marquee Tape */}
      <div
        style={{
          position: "absolute",
          top: 70,
          left: -100,
          right: -100,
          height: 48,
          backgroundColor: "#CCFF00",
          color: "#000000",
          display: "flex",
          alignItems: "center",
          transform: "rotate(-2deg)",
          overflow: "hidden",
          boxShadow: "0 8px 30px rgba(204,255,0,0.4)",
          zIndex: 40,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 32,
            whiteSpace: "nowrap",
            fontSize: 22,
            fontWeight: 900,
            letterSpacing: "0.15em",
            transform: `translateX(${tapeSlide}px)`,
          }}
        >
          <span>/// KYUSTORE DROP 2026 /// VERIFIED INSTANT /// 0.8s INJECT /// MLBB OFFICIAL ///</span>
          <span>/// KYUSTORE DROP 2026 /// VERIFIED INSTANT /// 0.8s INJECT /// MLBB OFFICIAL ///</span>
          <span>/// KYUSTORE DROP 2026 /// VERIFIED INSTANT /// 0.8s INJECT /// MLBB OFFICIAL ///</span>
        </div>
      </div>

      {/* 3. High-Impact Character Cutout Backdrop */}
      <div
        style={{
          position: "absolute",
          top: 240,
          left: 50,
          right: 50,
          height: 800,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10,
        }}
      >
        <Img
          src={staticFile("assets_library/dynamic_renders/valorant_jett_artwork.png")}
          style={{
            height: "100%",
            objectFit: "contain",
            filter: "contrast(1.15) drop-shadow(0 20px 50px rgba(0,0,0,0.9))",
            transform: `scale(${0.9 + introSpring * 0.1})`,
          }}
        />
        {/* Holographic Sticker on Character */}
        <div
          style={{
            position: "absolute",
            top: 60,
            right: 40,
            backgroundColor: "#FFFFFF",
            color: "#000000",
            padding: "8px 18px",
            fontSize: 20,
            fontWeight: 900,
            letterSpacing: "0.1em",
            border: "3px solid #000000",
            transform: "rotate(12deg)",
            boxShadow: "5px 5px 0px #CCFF00",
          }}
        >
          GENUINE AUTHENTIC
        </div>
      </div>

      {/* 4. Giant Brutalist Headline */}
      <div
        style={{
          position: "absolute",
          top: 960,
          left: 50,
          right: 50,
          zIndex: 30,
        }}
      >
        <div style={{ overflow: "hidden" }}>
          <div
            style={{
              fontSize: 108,
              lineHeight: 0.9,
              fontWeight: 900,
              textTransform: "uppercase",
              color: "#FFFFFF",
              letterSpacing: "-0.03em",
              transform: `translateY(${headTranslate}px)`,
            }}
          >
            TOP-UP <span style={{ color: "#CCFF00" }}>SATSET</span>
          </div>
        </div>
        <div style={{ overflow: "hidden" }}>
          <div
            style={{
              fontSize: 82,
              lineHeight: 0.95,
              fontWeight: 900,
              textTransform: "uppercase",
              color: "#A1A1AA",
              letterSpacing: "-0.02em",
              transform: `translateY(${headTranslate}px)`,
            }}
          >
            ANTI-BONCOS.
          </div>
        </div>
      </div>

      {/* 5. Brutalist Price & Spec Block (Overlapping) */}
      <div
        style={{
          position: "absolute",
          bottom: 240,
          left: 50,
          right: 50,
          backgroundColor: "#CCFF00",
          color: "#000000",
          padding: "24px 32px",
          border: "4px solid #FFFFFF",
          boxShadow: "10px 10px 0px #FFFFFF",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          transform: `scale(${Math.max(0, badgeScale)}) rotate(-1deg)`,
          zIndex: 50,
        }}
      >
        <div>
          <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: "0.1em" }}>
            WEEKLY DIAMOND PASS
          </div>
          <div style={{ fontSize: 62, fontWeight: 900, lineHeight: 1 }}>
            RP 27.500
          </div>
        </div>
        <div
          style={{
            backgroundColor: "#000000",
            color: "#CCFF00",
            padding: "12px 24px",
            fontSize: 26,
            fontWeight: 900,
            letterSpacing: "0.08em",
          }}
        >
          AMBIL SEKARANG ↗
        </div>
      </div>

      {/* 6. Footer Raw Meta & Barcode */}
      <div
        style={{
          position: "absolute",
          bottom: 70,
          left: 50,
          right: 50,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          zIndex: 40,
        }}
      >
        <div>
          <div style={{ fontSize: 16, fontFamily: "monospace", color: "#71717A" }}>
            BATCH_ID: KYU_2026_X9
          </div>
          <div style={{ fontSize: 16, fontFamily: "monospace", color: "#CCFF00" }}>
            MOONTON DIRECT API • STATUS: 200 OK
          </div>
        </div>
        {/* Fake Brutalist Barcode */}
        <div style={{ display: "flex", gap: 3, height: 36, alignItems: "flex-end" }}>
          {[4, 8, 2, 6, 3, 7, 2, 5, 8, 3, 2, 7, 4, 6, 2, 8, 5, 3].map((w, i) => (
            <div
              key={i}
              style={{
                width: w,
                height: "100%",
                backgroundColor: i % 3 === 0 ? "#CCFF00" : "#FFFFFF",
              }}
            />
          ))}
        </div>
      </div>
    </AbsoluteFill>
  );
};
